import ChartDownloadButton from "../../../components/ChartDownloadButton";
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useD3LineChart, type ChartDataPoint, type LineConfig } from "../../../hooks/useD3LineChart";
import type { PerformanceChartDataResult } from "../../../hooks/usePerformanceChartData";
import { benchmarkFactorsService } from "../../../services/benchmarkFactorsService";
import { DETAIL_BENCHMARKS, DEFAULT_BENCHMARK_SYMBOL } from "../../../config/benchmarkContracts";
import {
  applyAnnualFeeToCumulativeSeries,
  bucketFactorsToCumulativeSeries,
  getBucketMsForFilter,
  ALPHA_PERFORMANCE_SERIES_STROKES,
  type CumulativeSeriesPoint,
  type V3TimeFilter,
} from "../../../components/performanceChartV3Utils";
import V3PeriodFilterRow from "../../../components/V3PeriodFilterRow";
import V3PerformanceSummaryTable from "../../../components/V3PerformanceSummaryTable";
import type { FactorEntry } from "../../../services/productFactorsService";
import { formatSignedPercentage } from "../../../utils/numberUtils";
import { shareableLinksService } from "../../../services/shareableLinksService";
import type { BenchmarkContract } from "../../../config/benchmarkContracts";
import PerformanceChartSurface from "../../../components/PerformanceChartSurface";
import { RETRO_CHART_MARGINS, RETRO_LINE_STYLES, type PerformanceChartVisualStyle } from "../../../components/performanceChartAppearance";

// Stroke + swatch per detail-page benchmark. Tokens only (CLAUDE.md 16).
const BENCHMARK_STYLES: Record<string, { stroke: string; swatch: string }> = {
  SPX: { stroke: "var(--rp-chart-3)", swatch: "bg-rp-chart-3" },
  NDX: { stroke: "var(--rp-chart-4)", swatch: "bg-rp-chart-4" },
  IWM: { stroke: "var(--rp-chart-6)", swatch: "bg-rp-chart-6" },
};

interface BenchmarkModel {
  symbol: string;
  label: string;
  id: string;
  stroke: string;
  swatch: string;
  points: CumulativeSeriesPoint[];
}

export interface DetailPreparedChartInput {
  loading: boolean;
  error: string | null;
  chartHeight: number;
  chartData: ChartDataPoint[];
  selectedPeriod: string;
  entityType: PerformanceChartDataResult["entityType"];
}

export interface DetailProvidedBenchmarkSeries {
  benchmark: BenchmarkContract;
  points: CumulativeSeriesPoint[];
}

interface DetailNetBenchmarkChartCommonProps {
  // Fired when the user toggles a benchmark on/off (for analytics).
  onBenchmarkToggle?: (symbol: string, nowSelected: boolean) => void;
  benchmarkSource?: "authenticated" | "public";
  benchmarks?: BenchmarkContract[];
  compact?: boolean;
  showNet?: boolean;
  productLabel?: string;
  // Stroke and legend swatch for the product line, for hosts whose surface the
  // default (white) line cannot sit on, e.g. a light card. Token vars/classes only.
  productStroke?: string;
  productSwatchClassName?: string;
  showPeriodFilters?: boolean;
  compactMobilePeriodFilters?: boolean;
  chartActionsPlacement?: "inline" | "end";
  chartActionsAppearance?: "default" | "subtle";
  isDarkMode?: boolean;
  useUtcDateLabels?: boolean;
  portfolioName?: string;
  showWatermark?: boolean;
  ariaLabel?: string;
  presentation?: "detail" | "tile";
  visualStyle?: PerformanceChartVisualStyle;
  plotHeight?: number;
  // Opt in from Discover only; other hosts retain their own sizing and controls.
  matchShareMobileLayout?: boolean;
}

type DetailNetBenchmarkChartProps = DetailNetBenchmarkChartCommonProps & (
  | {
      chart: PerformanceChartDataResult;
      providedBenchmarkSeries?: undefined;
    }
  | {
      chart: DetailPreparedChartInput;
      providedBenchmarkSeries: DetailProvidedBenchmarkSeries[];
    }
);

function pointMs(point: { date: string; fullDate?: string }): number {
  return new Date(point.fullDate ?? point.date).getTime();
}

function lastValue(points: Array<{ performance?: number | null }>): number | null {
  if (points.length === 0) return null;
  const value = points[points.length - 1]?.performance;
  return typeof value === "number" ? value : null;
}

/**
 * Detail-page "Net & Benchmark" chart view. Overlays a net-of-fees product line
 * (1% annual) and toggleable benchmark indices (SPX default; NDX / Russell 2000
 * optional) on the gross product line, following the Analyze-page pattern.
 *
 * The gross series comes from the shared chart hook (already rebased to 0% at
 * the window start). The net line is a closed-form transform of it; each
 * benchmark is bucketed over the same window with the same helper, so all lines
 * share an axis. No new product fetch -- only benchmark factors are loaded.
 */
const DetailNetBenchmarkChart: React.FC<DetailNetBenchmarkChartProps> = (props) => {
  const {
    chart,
    onBenchmarkToggle,
    benchmarkSource = "authenticated",
    benchmarks = DETAIL_BENCHMARKS,
    compact = false,
    showNet = true,
    productLabel = "Gross",
    portfolioName,
    productStroke,
    productSwatchClassName,
    showPeriodFilters,
    providedBenchmarkSeries,
    isDarkMode: inputDarkMode = true,
    useUtcDateLabels = false,
    showWatermark,
    ariaLabel,
    presentation = "detail",
    visualStyle = "default",
  } = props;
  const isTilePresentation = presentation === "tile";
  const isRetro = visualStyle === "retro";
  const isDarkMode = isRetro ? false : inputDarkMode;
  const [mobilePlotHeight, setMobilePlotHeight] = useState<number | undefined>();
  const usesShareMobileLayout = isRetro && props.matchShareMobileLayout && mobilePlotHeight !== undefined;
  const plotHeight = props.plotHeight ?? (usesShareMobileLayout ? mobilePlotHeight : undefined);
  const [benchmarkData, setBenchmarkData] = useState<Record<string, FactorEntry[]>>({});
  const [selectedSymbols, setSelectedSymbols] = useState<string[]>([
    providedBenchmarkSeries?.[0]?.benchmark.symbol
      ?? benchmarks[0]?.symbol
      ?? DEFAULT_BENCHMARK_SYMBOL,
  ]);

  useEffect(() => {
    if (providedBenchmarkSeries !== undefined) return;

    let cancelled = false;
    const request = benchmarkSource === "public"
      ? Promise.all(benchmarks.map(async (benchmark) => {
          try {
            return {
              benchmark,
              factors: await shareableLinksService.getPublicContractFactors(benchmark.conId),
            };
          } catch {
            return { benchmark, factors: [] };
          }
        }))
      : benchmarkFactorsService.getBenchmarkFactorsBatch(benchmarks);

    request
      .then((results) => {
        if (cancelled) return;
        const next: Record<string, FactorEntry[]> = {};
        results.forEach(({ benchmark, factors }) => {
          next[benchmark.symbol] = factors;
        });
        setBenchmarkData(next);
      })
      .catch((err) => {
        if (!cancelled) console.error("Failed to load detail benchmark factors", err);
      });
    return () => {
      cancelled = true;
    };
  }, [benchmarkSource, benchmarks, providedBenchmarkSeries]);

  const grossPoints = chart.chartData ?? [];
  const startMs = grossPoints.length > 0 ? pointMs(grossPoints[0]) : Number.NaN;
  const anchorMs = grossPoints.length > 0 ? pointMs(grossPoints[grossPoints.length - 1]) : Number.NaN;
  const bucketMs = getBucketMsForFilter(chart.selectedPeriod as V3TimeFilter);

  const netPoints = useMemo(() => applyAnnualFeeToCumulativeSeries(grossPoints), [grossPoints]);

  const benchmarkModels = useMemo<BenchmarkModel[]>(() => {
    if (providedBenchmarkSeries !== undefined) {
      return providedBenchmarkSeries.map(({ benchmark, points }) => {
        const style = BENCHMARK_STYLES[benchmark.symbol]
          ?? { stroke: "var(--rp-chart-3)", swatch: "bg-rp-chart-3" };
        return {
          symbol: benchmark.symbol,
          label: benchmark.label,
          id: `bmk_${benchmark.symbol}`,
          stroke: style.stroke,
          swatch: style.swatch,
          points,
        };
      }).filter((model) => model.points.length > 1);
    }

    if (grossPoints.length === 0 || Number.isNaN(startMs) || Number.isNaN(anchorMs)) return [];
    return benchmarks.map((benchmark) => {
      const factors = benchmarkData[benchmark.symbol] ?? [];
      const inWindow = factors.filter((factor) => {
        const ms = new Date(factor.t).getTime();
        return !Number.isNaN(ms) && ms > startMs && ms <= anchorMs;
      });
      // Rebase the benchmark to 0% at its OWN earliest in-window point, not the
      // product's window start. When a benchmark's history is shorter than the
      // product's (e.g. SPX vs an older product), this keeps the line confined
      // to the period it actually covers instead of drawing a flat segment from
      // the product's inception to the benchmark's first data point.
      const benchStartMs = inWindow.length > 0
        ? inWindow.reduce((min, factor) => Math.min(min, new Date(factor.t).getTime()), Number.POSITIVE_INFINITY)
        : startMs;
      // Exclude the factor at benchStartMs itself: factor.t marks the END of its
      // return slot, so that factor is the move INTO the baseline point and is
      // pre-anchor. Counting it would inflate the rebased line (the bucket helper
      // floors the first index to 1, folding the boundary factor into the first
      // post-baseline bucket). The gross line does count its first factor, but its
      // anchor is genuine inception (no prior value); the benchmark anchor is
      // mid-history, so the boundary return is real and must be dropped.
      const benchFactors = inWindow.filter((factor) => new Date(factor.t).getTime() > benchStartMs);
      const points = bucketFactorsToCumulativeSeries(benchFactors, benchStartMs, bucketMs);
      const style = BENCHMARK_STYLES[benchmark.symbol] ?? { stroke: "var(--rp-chart-3)", swatch: "bg-rp-chart-3" };
      return {
        symbol: benchmark.symbol,
        label: benchmark.label,
        id: `bmk_${benchmark.symbol}`,
        stroke: style.stroke,
        swatch: style.swatch,
        points,
      };
    }).filter((model) => model.points.length > 1);
  }, [benchmarkData, benchmarks, grossPoints.length, startMs, anchorMs, bucketMs, providedBenchmarkSeries]);

  const selectedBenchmarkModels = useMemo(
    () => providedBenchmarkSeries !== undefined
      ? benchmarkModels
      : benchmarkModels.filter((model) => selectedSymbols.includes(model.symbol)),
    [benchmarkModels, providedBenchmarkSeries, selectedSymbols]
  );

  const chartData = useMemo<ChartDataPoint[]>(() => {
    const byTs = new Map<number, ChartDataPoint>();
    const ensure = (iso: string): ChartDataPoint => {
      const ms = new Date(iso).getTime();
      let point = byTs.get(ms);
      if (!point) {
        point = { date: iso, fullDate: iso };
        byTs.set(ms, point);
      }
      return point;
    };

    netPoints.forEach((p) => {
      const point = ensure(p.fullDate ?? p.date);
      point.performance = p.performance ?? null;
      point.performanceNet = p.performanceNet;
    });
    selectedBenchmarkModels.forEach((model) => {
      model.points.forEach((p) => {
        ensure(p.fullDate)[model.id] = p.performance;
      });
    });

    return [...byTs.entries()].sort((a, b) => a[0] - b[0]).map(([, point]) => point);
  }, [netPoints, selectedBenchmarkModels]);

  const lines = useMemo<LineConfig[]>(
    () => [
      {
        dataKey: "performance",
        label: productLabel,
        stroke: productStroke ?? (compact
          ? ALPHA_PERFORMANCE_SERIES_STROKES.strategy
          : "var(--rp-chart-total)"),
        strokeWidth: 2,
        showDots: false,
        swatchClassName: productSwatchClassName ?? (compact ? "bg-rp-amber" : "bg-rp-chart-total"),
      },
      ...(showNet ? [{
        dataKey: "performanceNet",
        label: "Net of fees (1%)",
        stroke: "var(--rp-chart-1)",
        strokeWidth: 2.5,
        showDots: false,
        swatchClassName: "bg-rp-chart-1",
      }] : []),
      ...selectedBenchmarkModels.map((model) => ({
        dataKey: model.id,
        label: model.label,
        stroke: model.stroke,
        strokeWidth: 1.5,
        strokeDasharray: "4 4",
        showDots: false,
        swatchClassName: model.swatch,
      })),
    ],
    [compact, productLabel, productStroke, productSwatchClassName, selectedBenchmarkModels, showNet]
  );

  const { svgRef, containerRef, tooltipRef } = useD3LineChart({
    visualStyle,
    data: chartData,
    lines,
    isDarkMode,
    height: chart.chartHeight,
    retroPlotHeight: plotHeight,
    margins: isTilePresentation
      ? { top: 4, right: 4, bottom: 4, left: 4 }
      : { top: 8, right: 72, bottom: 24, left: 12 },
    chartChrome: isTilePresentation ? "minimal" : "full",
    yAxisFormatter: (value: number) => formatSignedPercentage(value, 0),
    tooltipFormatter: (value: number) => formatSignedPercentage(value, 2),
    showLegend: false,
    renderingMode: "enhanced",
    useUtcDateLabels,
  });

  const chartVisible = !chart.loading && !chart.error && grossPoints.length > 0;
  useLayoutEffect(() => {
    if (!props.matchShareMobileLayout || !isRetro || !chartVisible) return;
    const surface = containerRef.current;
    if (!surface) return;
    let frame = 0;
    const measure = () => {
      const width = surface.offsetWidth;
      if (!width) return;
      // PWA-3087: match Share's square chart below Tailwind's 640px sm breakpoint.
      // Pass the same plot height to D3 and the surface; CSS alone leaves the SVG wide.
      setMobilePlotHeight(window.innerWidth < 640
        ? Math.max(0, width - RETRO_CHART_MARGINS.top - RETRO_CHART_MARGINS.bottom)
        : undefined);
    };
    const scheduleMeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(scheduleMeasure);
    observer?.observe(surface);
    measure();
    window.addEventListener("resize", scheduleMeasure);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [props.matchShareMobileLayout, isRetro, chartVisible, containerRef]);

  const shouldShowWatermark = showWatermark ?? (isRetro || (compact && !isTilePresentation));
  const toggleBenchmark = (symbol: string) => {
    const nowSelected = !selectedSymbols.includes(symbol);
    setSelectedSymbols((prev) =>
      prev.includes(symbol) ? prev.filter((s) => s !== symbol) : [...prev, symbol]
    );
    onBenchmarkToggle?.(symbol, nowSelected);
  };
  const legendItems = (
    <>
      <LegendItem
        retroStyle={isRetro ? RETRO_LINE_STYLES.performance : undefined}
        label={productLabel}
        bold={isRetro}
        swatchClassName={productSwatchClassName ?? (compact ? "bg-rp-amber" : "bg-rp-chart-total")}
        value={lastValue(netPoints)}
        showValue={!isTilePresentation}
        isSelected
        isDarkMode={isDarkMode}
      />
      {showNet && (
        <LegendItem
          retroStyle={isRetro ? RETRO_LINE_STYLES.performanceNet : undefined}
          label="Net of fees (1%)"
          bold={isRetro}
          swatchClassName="bg-rp-chart-1"
          value={netPoints.length > 0 ? netPoints[netPoints.length - 1].performanceNet : null}
          showValue={!isTilePresentation}
          isSelected
          isDarkMode={isDarkMode}
        />
      )}
      {benchmarkModels.map((model) => (
        <LegendItem
          key={model.symbol}
          retroStyle={isRetro ? RETRO_LINE_STYLES[model.id] : undefined}
          label={model.label}
          swatchClassName={model.swatch}
          value={lastValue(model.points)}
          showValue={!isTilePresentation}
          isSelected={providedBenchmarkSeries !== undefined || selectedSymbols.includes(model.symbol)}
          onClick={
            !isTilePresentation && providedBenchmarkSeries === undefined
              ? () => toggleBenchmark(model.symbol)
              : undefined
          }
          isDarkMode={isDarkMode}
        />
      ))}
    </>
  );

  if (chart.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className={`h-8 w-8 animate-spin rounded-full border-b-2 ${isDarkMode ? "border-slate-400" : "border-v7-ink/30"}`} />
        <span className={`ml-3 text-sm ${isDarkMode ? "text-gray-300" : "text-v7-ink/60"}`}>Loading performance data...</span>
      </div>
    );
  }

  if (chart.error) {
    return (
      <div className={`rounded-lg border p-4 ${isDarkMode ? "border-red-800 bg-red-900/20" : "border-red-200 bg-red-50"}`}>
        <p className={`text-sm ${isDarkMode ? "text-red-400" : "text-red-700"}`}>{chart.error}</p>
      </div>
    );
  }

  if (grossPoints.length === 0) {
    return (
      <div className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-v7-ink/60"}`}>
        <p>No performance data available for this {chart.entityType.toLowerCase()}.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {isTilePresentation && (
        <div
          data-testid="detail-chart-tile-legend"
          className="pointer-events-none mb-1.5 flex flex-wrap items-center gap-x-4 gap-y-1"
        >
          {legendItems}
        </div>
      )}
      {isRetro ? (
        <PerformanceChartSurface
          portfolioName={portfolioName}
          plotHeight={plotHeight}
          containerRef={containerRef}
          svgRef={svgRef}
          tooltipRef={tooltipRef}
          selectedPeriod={chart.selectedPeriod}
          legend={legendItems}
          showWatermark={shouldShowWatermark}
          ariaLabel={ariaLabel}
        />
      ) : <div
        ref={containerRef}
        data-chart-presentation={presentation}
        style={{
          width: "100%",
          height: chart.chartHeight,
          position: "relative",
          overflow: isTilePresentation ? "hidden" : "visible",
        }}
      >
        <svg ref={svgRef} role={ariaLabel ? "img" : undefined} aria-label={ariaLabel} style={{ display: "block" }} />
        {/* Legend overlaid inside the chart window, top-left. Wrapper is
            non-interactive so it never blocks the hover overlay; only the
            benchmark toggle buttons capture clicks. */}
        {!isTilePresentation && (
          <div className="pointer-events-none absolute left-2 right-2 top-2 z-10 flex flex-wrap items-center gap-x-4 gap-y-1 sm:left-3">
            {legendItems}
          </div>
        )}
        {shouldShowWatermark && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-10">
            <img src="/dark.png" alt="" className="h-10 w-auto" />
          </div>
        )}
        <div
          ref={tooltipRef}
          className={`pointer-events-none absolute z-[1000] hidden rounded-md border px-3 py-2 shadow-lg ${
            isDarkMode
              ? "border-slate-700 bg-slate-950"
              : "border-v7-ink/15 bg-v7-white"
          }`}
        />
      </div>}

      {(showPeriodFilters ?? !compact) && props.providedBenchmarkSeries === undefined && <V3PeriodFilterRow
        actions={isRetro ? <ChartDownloadButton containerRef={containerRef} portfolioName={portfolioName} appearance={usesShareMobileLayout ? "subtle" : props.chartActionsAppearance} /> : undefined}
        actionsPlacement={usesShareMobileLayout ? "end" : props.chartActionsPlacement}
        compactOnMobile={usesShareMobileLayout || props.compactMobilePeriodFilters}
        timeFilterOptions={props.chart.timeFilterOptions}
        availableFilters={props.chart.availableFilters}
        selectedPeriod={props.chart.selectedPeriod}
        handleSelectPeriod={props.chart.handleSelectPeriod}
        v3IntrinsicAvailability={props.chart.v3IntrinsicAvailability}
        v3LoadedCoverage={props.chart.v3LoadedCoverage}
        v3HasDataByPeriod={props.chart.v3HasDataByPeriod}
        isDarkMode={isDarkMode}
      />}

      {!compact && props.providedBenchmarkSeries === undefined && props.chart.formattedCreateDate && (
        <div className={`mt-2 text-center text-[11px] ${isDarkMode ? "text-gray-400" : "text-v7-ink/60"}`}>
          Inception: {props.chart.formatLocalDate(props.chart.formattedCreateDate)}
        </div>
      )}

      {!compact && props.providedBenchmarkSeries === undefined && props.chart.performanceMetrics.length > 0 && (
        <V3PerformanceSummaryTable
          orderedFiltersForTable={props.chart.orderedFiltersForTable}
          timeframePerformance={props.chart.timeframePerformance}
          formatPerformance={props.chart.formatPerformance}
          isDarkMode={isDarkMode}
          showGrossOfFeesNote={showNet}
        />
      )}
    </div>
  );
};

function LegendItem({
  label,
  value,
  swatchClassName,
  isSelected,
  onClick,
  isDarkMode,
  showValue = true,
  bold = false,
  retroStyle,
}: {
  label: string;
  value: number | null;
  swatchClassName: string;
  isSelected: boolean;
  onClick?: () => void;
  isDarkMode: boolean;
  showValue?: boolean;
  bold?: boolean;
  retroStyle?: Pick<LineConfig, "stroke" | "strokeWidth" | "strokeDasharray">;
}) {
  const content = (
    <>
      {retroStyle ? (
        <svg width="14" height="8" aria-hidden="true" className="shrink-0">
          <line x1="0" x2="14" y1="4" y2="4" {...retroStyle} />
        </svg>
      ) : <span className={`h-2 w-2 shrink-0 rounded-full ${swatchClassName}`} />}
      <span className={retroStyle ? `text-[11px] leading-3 text-retro-ink ${bold ? "font-bold" : ""}` : `text-[10px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-gray-300" : "text-v7-ink/60"}`}>{label}</span>
      {showValue && (
        <span className={retroStyle ? `text-[11px] leading-3 text-retro-ink ${bold ? "font-bold" : ""}` : `font-mono text-[10px] ${isDarkMode ? "text-gray-200" : "text-v7-ink"}`}>
          {value == null ? "--" : formatSignedPercentage(value, 1)}
        </span>
      )}
    </>
  );

  if (!onClick) {
    return <div className={`flex items-center opacity-100 ${retroStyle ? "gap-1.5 whitespace-nowrap bg-retro-paper/90" : "gap-2"}`}>{content}</div>;
  }

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={onClick}
      className={`pointer-events-auto flex items-center transition-opacity ${retroStyle ? "gap-1.5 whitespace-nowrap bg-retro-paper/90" : "gap-2"} ${
        isSelected ? "opacity-100" : "opacity-50 hover:opacity-90"
      }`}
    >
      {content}
    </button>
  );
}

export default DetailNetBenchmarkChart;
